/**
 * Компоненты интерфейса. Тонкие обёртки над классами из tokens.css, а не
 * отдельная система стилей: так экран можно сверить с прототипом глазами, а
 * вся визуальная логика остаётся в одном CSS-файле.
 */
import { tr } from './format';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Icon, type IconName } from './icons';

export function Card({
  children,
  className = '',
  style,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  return (
    <Tag className={`card ${className}`} style={style}>
      {children}
    </Tag>
  );
}

type ButtonVariant = 'pri' | 'out' | 'ghost' | 'danger';

export function Button({
  children,
  variant = 'out',
  size,
  block,
  icon,
  onClick,
  disabled,
  type = 'button',
  title,
  ariaLabel,
}: {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: 'sm' | 'lg';
  block?: boolean;
  /** Иконка слева от текста, или единственное содержимое кнопки. */
  icon?: IconName;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  ariaLabel?: string;
}) {
  const iconOnly = Boolean(icon) && children === undefined;
  const classes = ['btn', variant, size ?? '', block ? 'block' : '', iconOnly ? 'icon' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel ?? (iconOnly ? title : undefined)}
    >
      {icon && <Icon name={icon} size={size === 'sm' ? 14 : 16} />}
      {children}
    </button>
  );
}

export function Badge({
  children,
  tone,
  dot,
  icon,
}: {
  children: ReactNode;
  tone?: 'live' | 'good' | 'warn' | 'bad' | 'prize' | 'accent';
  dot?: boolean;
  icon?: IconName;
}) {
  return (
    <span className={`badge ${tone ?? ''}`}>
      {dot && <i className="dt" />}
      {icon && <Icon name={icon} size={12} />}
      {children}
    </span>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div className="seg" role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          aria-selected={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function KeyRow({
  label,
  children,
  mono,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="krow">
      <span className="k">{label}</span>
      <span className={`v ${mono ? 'mono' : ''}`}>{children}</span>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className={`field ${error ? 'invalid' : ''}`}>
      {label && <span className="lab">{label}</span>}
      {children}
      {error ? <span className="err">{error}</span> : hint ? <span className="sub">{hint}</span> : null}
    </label>
  );
}

export function Empty({
  icon = 'sparkle',
  title,
  children,
}: {
  icon?: IconName;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="ic">
        <Icon name={icon} size={18} />
      </div>
      <div className="h2">{title}</div>
      {children && (
        <div className="sub" style={{ marginTop: 5, maxWidth: 360, marginInline: 'auto' }}>
          {children}
        </div>
      )}
    </div>
  );
}

export function Skeleton({ height = 60, count = 1 }: { height?: number; count?: number }) {
  return (
    <div className="stack" aria-hidden>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skel" style={{ height }} />
      ))}
    </div>
  );
}

/** Клавиатурный шорткат. Linear показывает их везде, где действие имеет клавишу. */
export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd">{children}</kbd>;
}

/** Аватар-монограмма. */
export function Avatar({ name, size = 22 }: { name: string; size?: number }) {
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
      aria-hidden
    >
      {initials(name)}
    </span>
  );
}

/** Подсказка при наведении. Чистый CSS — без слоя позиционирования. */
export function Tooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="tip">
      {children}
      <span className="tipbody" role="tooltip">
        {label}
      </span>
    </span>
  );
}

/**
 * Модалка / нижняя шторка.
 *
 * Закрывается по Escape и клику по фону, переносит фокус внутрь и возвращает
 * его на место при закрытии. В прототипе шторки недоступны с клавиатуры, а
 * оформление приза проходить мышью необязательно.
 */
export function Sheet({
  title,
  onClose,
  children,
  footer,
}: {
  title?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<Element | null>(null);

  useEffect(() => {
    returnFocus.current = document.activeElement;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (returnFocus.current instanceof HTMLElement) returnFocus.current.focus();
    };
  }, [onClose]);

  return (
    <div className="ovl" onClick={onClose} role="presentation">
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="grab" />
        {title && (
          <div className="row" style={{ marginBottom: 12 }}>
            <span className="h1" style={{ fontSize: 16, flex: 1 }}>
              {title}
            </span>
            <Button variant="ghost" size="sm" icon="close" title={tr('Закрыть', 'Close', 'Cerrar')} onClick={onClose} />
          </div>
        )}
        {children}
        {footer && <div style={{ marginTop: 14 }}>{footer}</div>}
      </div>
    </div>
  );
}

/** Обратный отсчёт в формате «3д 04:12:08». */
export function Countdown({ to, prefix }: { to: string | number | Date; prefix?: string }) {
  const target = typeof to === 'object' ? to.getTime() : new Date(to).getTime();
  const [, tick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  if (!Number.isFinite(target)) return null;
  const ms = Math.max(0, target - Date.now());
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const pad = (n: number) => String(n).padStart(2, '0');
  const text =
    (days > 0 ? `${days}${tr('д', 'd', 'd')} ` : '') +
    `${pad(Math.floor((total % 86400) / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;

  return (
    <span className="mono">
      {prefix ? `${prefix} ` : ''}
      {text}
    </span>
  );
}

/** Переключатель темы. Без явного выбора действует предпочтение ОС. */
export function ThemeToggle() {
  const [theme, setTheme] = useState<'dark' | 'light' | null>(() => {
    try {
      const stored = localStorage.getItem('prizma-theme');
      return stored === 'dark' || stored === 'light' ? stored : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme) {
      root.setAttribute('data-theme', theme);
      try {
        localStorage.setItem('prizma-theme', theme);
      } catch {
        /* приватный режим: выбор просто не сохранится */
      }
    } else {
      root.removeAttribute('data-theme');
    }
  }, [theme]);

  const resolved =
    theme ?? (window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark');

  return (
    <Button
      variant="ghost"
      size="sm"
      icon={resolved === 'dark' ? 'moon' : 'sun'}
      title={tr('Переключить тему', 'Switch theme', 'Cambiar el tema')}
      onClick={() => setTheme(resolved === 'dark' ? 'light' : 'dark')}
    />
  );
}

/** Всплывающее подтверждение действия. */
export function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const id = setTimeout(onDone, 2600);
    return () => clearTimeout(id);
  }, [onDone]);

  return (
    <div className="toast" role="status">
      <Icon name="checkCircle" size={14} />
      {message}
    </div>
  );
}

/**
 * Хеш или сид: сокращён, с полным значением в подсказке и копированием в один
 * клик. Хеши здесь нужно сверять глазами, поэтому обрезать их молча нельзя.
 */
export function Hash({ value, chars = 8 }: { value: string; chars?: number }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="faint">—</span>;

  const short = value.length > chars * 2 ? `${value.slice(0, chars)}…${value.slice(-chars)}` : value;

  const copy = () => {
    navigator.clipboard
      ?.writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      })
      .catch(() => {
        /* без разрешения на буфер обмена — просто ничего не произойдёт */
      });
  };

  return (
    <button
      className="mono"
      title={`${value}\n\n${tr('Нажми, чтобы скопировать', 'Click to copy', 'Pulsa para copiar')}`}
      onClick={copy}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        color: 'inherit',
        cursor: 'pointer',
      }}
    >
      {short}
      <Icon name={copied ? 'check' : 'copy'} size={11} style={{ opacity: copied ? 1 : 0.45 }} />
    </button>
  );
}

export function Progress({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="track" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

/** Логотип бренда, либо монограмма, если логотипа нет. */
export function BrandMark({
  name,
  logoUrl,
  size = 28,
}: {
  name: string;
  logoUrl?: string;
  size?: number;
}) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        width={size}
        height={size}
        style={{
          objectFit: 'contain',
          // Белая подложка под логотип — намеренно и в обеих темах: логотипы
          // брендов обычно тёмные на прозрачном фоне и на тёмной поверхности
          // просто исчезают.
          background: '#fff',
          borderRadius: Math.max(4, size / 4.5),
          padding: Math.max(2, size / 11),
          flex: 'none',
        }}
      />
    );
  }
  return (
    <div
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: Math.max(4, size / 4.5),
        background: 'var(--surface-2)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--lift)',
        display: 'grid',
        placeItems: 'center',
        fontWeight: 590,
        fontSize: size * 0.36,
        letterSpacing: '-0.02em',
        color: 'var(--dim)',
        flex: 'none',
      }}
    >
      {initials(name)}
    </div>
  );
}

export function initials(name: string): string {
  const words = name.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return (words[0] as string).slice(0, 2).toUpperCase();
  return ((words[0] as string)[0]! + (words[1] as string)[0]!).toUpperCase();
}
